import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Switch,
  Checkbox,
  FormControlLabel,
  TextField,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Stack,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CodeIcon from "@mui/icons-material/Code";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import BuildIcon from "@mui/icons-material/Build";

import api from "./../api";
import { MONO } from "./../theme";
import { PageHeader } from "./../components/ui";
import CodeEditorModal from "./../components/CodeEditorModal";
import NewGeneratorModal from "./../components/NewGeneratorModal";
import ProvisionLogModal from "./../components/ProvisionLogModal";

const VENV_COLOR = { ready: "success", missing: "warning", provisioning: "info", error: "error" };
const FALLBACK_GPUS = [{ index: 0 }, { index: 1 }];

export default function Generators() {
  const [gens, setGens] = useState([]);
  const [gpus, setGpus] = useState([]);
  const [codeName, setCodeName] = useState(null);
  const [creating, setCreating] = useState(false);
  const [provisionName, setProvisionName] = useState(null); // live build-log viewer

  const load = useCallback(() => {
    api.get("/api/generators", { silent: true }).then((d) => setGens(d.generators)).catch(() => {});
    api.get("/api/gpus", { silent: true }).then((d) => setGpus(d.gpus)).catch(() => {});
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  // While any venv is building, poll so the status chip flips to ready/error live.
  const anyProvisioning = gens.some((g) => g.venv_status === "provisioning");
  useEffect(() => {
    if (!anyProvisioning) return undefined;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [anyProvisioning, load]);

  const patch = async (name, body) => {
    try {
      await api.patch(`/api/generators/${name}`, body);
      load();
    } catch {
      /* api wrapper toasts */
    }
  };

  const rebuild = async (name) => {
    try {
      await api.patch(`/api/generators/${name}`, { reprovision: true });
      toast.info(`Rebuilding venv for ${name}…`);
      load();
    } catch {
      /* api wrapper toasts */
    }
  };

  const toggleGpu = (g, idx) => {
    const cur = new Set(g.eligible_gpus || []);
    cur.has(idx) ? cur.delete(idx) : cur.add(idx);
    patch(g.name, { eligible_gpus: [...cur] });
  };

  const del = async (name) => {
    if (!window.confirm(`Delete ${name}?`)) return;
    try {
      await api.del(`/api/generators/${name}`);
      load();
    } catch {
      /* api wrapper toasts */
    }
  };

  const gpuChoices = gpus.length ? gpus : FALLBACK_GPUS;

  return (
    <Box>
      <PageHeader
        eyebrow="Generate"
        title="Generators"
        subtitle="Image and speech models, each with its own venv. Pick eligible GPUs, set a VRAM estimate, and build the venv on demand."
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreating(true)}>
            New generator
          </Button>
        }
      />

      <Card>
        <CardContent>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>venv</TableCell>
                <TableCell>VRAM est.</TableCell>
                <TableCell>Eligible GPUs</TableCell>
                <TableCell>Enabled</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {gens.map((g) => (
                <TableRow key={g.id}>
                  <TableCell>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <Typography component="span" sx={{ fontFamily: MONO, fontWeight: 600, fontSize: ".85rem" }}>
                        {g.name}
                      </Typography>
                      {g.source === "custom" && (
                        <Chip label="custom" size="small" sx={{ height: 18, fontSize: ".68rem" }} />
                      )}
                      {g.warm && (
                        <Tooltip title="Model is kept loaded — next run skips the reload (offloaded models live in system RAM, not VRAM)">
                          <Chip label="⚡ warm" size="small" color="info" variant="outlined"
                                sx={{ height: 18, fontSize: ".68rem" }} />
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ color: "text.secondary" }}>{g.media_type}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <Chip
                        label={g.venv_status}
                        size="small"
                        color={VENV_COLOR[g.venv_status] || "default"}
                        sx={{ height: 20, fontSize: ".7rem" }}
                      />
                      <Typography variant="caption" sx={{ fontFamily: "monospace", color: "text.secondary" }}>
                        {g.venv_name || ""}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      type="number"
                      defaultValue={g.vram_estimate_mb}
                      onBlur={(e) => patch(g.name, { vram_estimate_mb: Number(e.target.value) })}
                      sx={{ width: 100 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", alignItems: "center" }}>
                      {gpuChoices.map((x) => (
                        <FormControlLabel
                          key={x.index}
                          sx={{ m: 0 }}
                          control={
                            <Checkbox
                              size="small"
                              sx={{ p: 0.25 }}
                              checked={(g.eligible_gpus || []).includes(x.index)}
                              onChange={() => toggleGpu(g, x.index)}
                            />
                          }
                          label={<Typography variant="caption">{x.index}</Typography>}
                        />
                      ))}
                      {(!g.eligible_gpus || !g.eligible_gpus.length) && (
                        <Typography variant="caption" color="text.secondary">
                          any
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Switch
                      size="small"
                      checked={!!g.enabled}
                      onChange={(e) => patch(g.name, { enabled: e.target.checked })}
                    />
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<CodeIcon sx={{ fontSize: 16 }} />}
                        onClick={() => setCodeName(g.name)}
                      >
                        Edit
                      </Button>
                      {g.venv_name &&
                        (g.venv_status === "provisioning" ? (
                          <Button
                            size="small"
                            variant="outlined"
                            color="info"
                            startIcon={<CircularProgress size={14} />}
                            onClick={() => setProvisionName(g.name)}
                          >
                            Building…
                          </Button>
                        ) : (
                          <Tooltip title={`Rebuild venv (${g.venv_name})`}>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<BuildIcon sx={{ fontSize: 16 }} />}
                              onClick={() => {
                                rebuild(g.name);
                                setProvisionName(g.name);
                              }}
                            >
                              Rebuild
                            </Button>
                          </Tooltip>
                        ))}
                      {g.source === "custom" && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />}
                          onClick={() => del(g.name)}
                        >
                          Del
                        </Button>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {codeName && (
        <CodeEditorModal
          name={codeName}
          onClose={(changed, reprovisioned) => {
            const nm = codeName;
            setCodeName(null);
            if (changed) load();
            if (reprovisioned) setProvisionName(nm); // stream the rebuild it kicked off
          }}
        />
      )}
      {creating && (
        <NewGeneratorModal
          onClose={(changed) => {
            setCreating(false);
            if (changed) load();
          }}
        />
      )}
      {provisionName && (
        <ProvisionLogModal
          endpoint={`/api/generators/${provisionName}/provision`}
          title={provisionName}
          onClose={() => {
            setProvisionName(null);
            load();
          }}
        />
      )}
    </Box>
  );
}
